// Phase C.5c — pure helpers behind the tap/select-first time input
// (Hour / Minute / AM-PM) that replaces the native `type="time"` keyboard
// entry in GameFormDialogs.js's Add/Edit Event form. Kept as plain
// functions (no React) so the actual 12h<->24h conversion logic is
// unit-testable without a brittle component test — see
// timeInput.test.js. SQL storage is untouched: the form still holds
// (and every RPC still receives) a plain 24-hour 'HH:MM' string exactly
// as before; this is a presentation-only reshaping of the SAME value.

/** Every selectable minute, in 5-minute increments — deliberately NOT
 * limited to 00/15/30/45: a real football schedule has legitimate times
 * like 5:10 or 6:40, and 5-minute granularity already covers both. */
export const STANDARD_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5);

/** 'HH:MM' (24h, as stored) -> { hour12 (1-12), minute (0-59), period
 * ('AM'|'PM') }, or null for an empty/unparseable value — the field's
 * own "nothing chosen yet" state, never a fabricated default time. */
export function parseTimeString(value) {
  if (!value) return null;
  const [hStr, mStr] = String(value).split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { hour12, minute: m, period };
}

/** The inverse of parseTimeString() — { hour12, minute, period } -> the
 * same 'HH:MM' 24h shape every RPC already expects. Assumes all three
 * parts are present (the caller's job to wait for a complete selection
 * before calling this — see TimeSelectField in GameFormDialogs.js). */
export function buildTimeString({ hour12, minute, period }) {
  let h = hour12 % 12;
  if (period === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** The minute choices to render for a given CURRENT minute value —
 * always the standard 5-minute increments, plus the current value
 * itself when it falls off that grid (e.g. a time saved before this
 * phase, like a legacy :07), so opening Edit on an existing event never
 * silently rounds its stored minute merely by rendering the field. */
export function minuteOptionsFor(currentMinute) {
  if (currentMinute === null || currentMinute === undefined || STANDARD_MINUTE_OPTIONS.includes(currentMinute)) {
    return STANDARD_MINUTE_OPTIONS;
  }
  return [...STANDARD_MINUTE_OPTIONS, currentMinute].sort((a, b) => a - b);
}
